// Profile of Adrian Eisenmeier, English. Source of the profile page and of the PDF in this language.
module.exports = {
  headline: 'Diploma Physicist • AI Expert • Software Developer',
  summary: 'Adrian Eisenmeier builds systems and advises where AI, software architecture, data protection and security meet in practice: Intent-Driven Development for AI-assisted software development, local PII anonymisation, finance analytics and hands-on training.',
  imageAlt: 'Portrait of Adrian Eisenmeier',
  personal: {
    position: 'Founder and Managing Director of pAIoneer Labs GmbH',
    birthdate: '13 Jun 1985',
    nationality: 'German',
    maritalStatus: 'Single',
    languages: 'German (native), English (fluent), Dutch (basic)',
    programmingLanguages: 'Python (16 years), R (17 years), C# (9 years), C/C++ (11 years), JavaScript full stack (React, TypeScript) (5 years)'
  },
  education: [
    {
      period: '2007 - 2016',
      field: 'Studies: Physics, Meteorology',
      institution: 'Albert-Ludwigs-University Freiburg, Germany',
      degree: 'Degree: Diploma in Physics'
    }
  ],
  skills: [
    {
      title: 'Agentic AI & LLM Engineering',
      description: 'Agentic AI systems, Intent-Driven Development (IDD), OpenCode and Claude Code workflows, agent instructions and skills, Model Context Protocol (MCP) with Python, TypeScript, PHP and C#, local LLM stacks with vLLM, Ollama and Hugging Face, RAG/GraphRAG/Agentic RAG architectures, RAG evals, Explainable AI, prompt engineering, model quantisation and fine-tuning with PEFT/LoRA'
    },
    {
      title: 'Data Science, Machine Learning & Signal Analytics',
      description: 'Scikit-Learn, H2O, TensorFlow-Keras, PyTorch, SciPy, ML.NET, Random Forest, LSTM, neural networks, Boltzmann, Hopfield and Jordan networks, computer vision and image processing with OpenCV, SkiaSharp and Pillow, SIFT, SURF, ORB, Laplace and Fourier transforms, signal-to-noise analysis, time-series analysis, forecasting, ARIMA and pyramid Auto-ARIMA, NLP, service-call analysis, KPI aggregation, predictive models, FAME migration, Chou Combination Index and machine learning for medical technology and pharmacy'
    },
    {
      title: 'Software Engineering, Frameworks & UI',
      description: 'Python, R, C#, C++, C, JavaScript, TypeScript, React, Java, Android, Lua, Bash, PHP, MATLAB, Mathematica, .NET 6/8, C++20, C++/CLI, BeeWare, Toga UI, Xamarin Forms, MAUI, HTML5, CSS, desktop, mobile and web UIs, software architecture, backend and full-stack architecture, event-driven architecture and Digital Twins'
    },
    {
      title: 'API Design, Services & Integration',
      description: 'ASP.NET/ASP, FastAPI, Flask API, Plumber, REST APIs, gRPC, Swagger/OpenAPI, API design, service contracts, REST architectures close to microservices, C# gRPC services with reflection, Python FastAPI backends, Taiga, Jira and Azure DevOps API integrations, robotics and IoT service integrations, authentication and backend integration'
    },
    {
      title: 'Databases, ORM & Persistence',
      description: 'SQL, SQL Server, SQLite, MySQL, Azure SQL, Azure Cosmos DB, Entity Framework and Entity Framework Core as ORM layer, SQL databases for anonymisation, time-series, tick, market, weather and energy data, vector databases with ChromaDB and Qdrant, RAG document indexes, data-quality checks and reporting validation'
    },
    {
      title: 'Cloud, DevOps & Infrastructure',
      description: 'Linux and Windows server administration, GPU and edge-AI infrastructure, Docker, Docker Compose, Podman, Kubernetes, container services, local on-premises LLM stacks, Azure Functions, IoT Hub, Event Hub, Digital Twins, Key Vault and Time Series Insights, AWS EC2, ECS, S3 and Lambda, Azure DevOps, GitLab pipelines, GitLab runners and executors, Caddy, Nginx, Let\'s Encrypt, local test environments and container orchestration on sensor hardware'
    },
    {
      title: 'Dashboards, Reporting & Monitoring',
      description: 'R Shiny, Python Shiny, ShinyProxy, Grafana, Kibana, CheckMK dashboards, Zabbix, RMarkdown, Shiny log-analysis dashboards, Kibana monitoring, KPI and process visualisation, controlling systems, statistical reports, daily reporting validation and operational monitoring views'
    },
    {
      title: 'Security, Data Protection & CRA',
      description: 'Cyber Resilience Act (CRA) expert, CRA readiness, secure-by-design and security-by-default, SBOM and software supply-chain security, threat modeling, vulnerability management, penetration testing and IT security (Kali, Tails Linux, Metasploit Framework, Nmap), IoT/OT security, Bluetooth Low Energy, Bluetooth Mesh, MQTT, Mosquitto, Presidio, PII and data protection'
    },
    {
      title: 'Project Methodology & AI Transformation',
      description: 'Lean AI, use-case evaluation, ROI/TCO calculation, process classification, Intent-Driven Development enablement, distinction between IDD and Spec-Driven Development, professional replacement of vibe coding, agile development, test-driven development, Scrum, Redmine, Jira, Taiga, Mantis, requirements engineering, stakeholder communication, release communication, Confluence and Doxygen documentation'
    }
  ],
  positions: [
    {
      period: 'January 2026 - Present',
      title: 'AI Engineer, Software Architect, Defence Industry Consultant',
      technologies: 'CUDA, vLLM, Ollama, Hugging Face, MCP Python, MCP TypeScript, MCP PHP, CI/CD GitLab pipelines, Docker, vector databases (ChromaDB, Qdrant), RAG / GraphRAG / Agentic RAG architectures, RAG evals, Explainable AI, Intent-Driven Development, Lean AI, OpenCode, Claude Code',
      description: [
        'Advisory role in creating and planning a company-wide AI strategy. Evaluation and definition of AI use cases according to Lean AI project-management processes.',
        'Calculation of return on investment and classification of AI use cases by process type and economic viability.',
        'Agentic software development and edge computing: planning and implementation of local alternatives to Anthropic to provide development teams with an on-premises OpenCode experience equivalent to Claude Code, especially for AI use cases involving data classified as VS-NfD (classified information - for official use only).',
        'Migration of Claude Code agent instructions and skill sets to OpenCode agent instructions and OpenCode skills.',
        'Planning of hardware and procurement costs for requirements where 30-person development teams develop agentically according to IDD in parallel with a time to first token (TTFT) below 10 seconds, using local LLMs that approach Claude Code in SWE benchmarks.',
        'After completed planning, concrete implementation of agentic, multimodal AI use cases.',
        'Training software developers in Intent-Driven Development (IDD), distinguishing it from Spec-Driven Development, and workshops on the benefits of IDD.',
        'Establishment of structured development processes for AI-assisted software development as a professional replacement for uncontrolled vibe coding.'
      ]
    },
    {
      period: 'December 2025 - April 2026',
      title: 'Software Developer, Banking',
      technologies: 'R Shiny',
      description: [
        'Implementation of an R Shiny dashboard for visualising and evaluating Global Banking Simulations (GBS).'
      ]
    },
    {
      period: 'September 2025 - December 2025',
      title: 'Software Developer / AI Engineer, Association of Statutory Health Insurance Dentists Baden-Württemberg (KZV BW)',
      technologies: 'Python, Microsoft Presidio SDK, spaCy NLP models, FastAPI, Beeware, Docker, SQL, data protection / personally identifiable information (PII)',
      description: [
        'Design and implementation of a generic PII software system that supports all languages worldwide to identify, anonymise and de-anonymise personal and confidential data in Office documents.',
        'Use of spaCy NLP models and the Microsoft Presidio Data Protection and De-identification SDK. A SQL database stores clear-text/anonymisation pairs. Delivery as a dockerised FastAPI with Swagger documentation, a standalone Python library and Windows/macOS standalone applications with Beeware and native Toga UI.'
      ]
    },
    {
      period: 'August 2025 - December 2025',
      title: 'Software Developer / AI Engineer, Industrial IoT',
      technologies: 'Python, Docker, Podman, FastAPI, OpenCV, Shiny, Kibana, HTML, CSS, Lua, embedded engineering',
      description: [
        'Senior software developer for a smart stereoscopic camera with AI hardware based on Hailo processors.',
        'Real-time object detection and evaluation in Python and Lua. Development of a container orchestration API directly on sensor hardware. Shiny log-analysis dashboard and Kibana monitoring, containerised and deployed on sensor electronics. Camera-hosted web application with HTML, CSS and JavaScript.'
      ]
    },
    {
      period: 'January 2025 - Present',
      title: 'Software Developer / AI Engineer, KRIKO Engineering GmbH',
      technologies: 'C#, gRPC, Simatic PCS7, SQL, JavaScript, React, Docker, Python, FastAPI, Swagger, PyTorch, CUDA, RAG, GRAG, agents, Hugging Face, R Shiny, GitLab, GitLab runners and executors, Azure DevOps, Taiga and Jira API',
      description: [
        'Senior software architect for multifunctional agentic AI systems and DevOps automation. Development of C# gRPC services with reflection and autonomous AI agents for voice-controlled code processing in Azure DevOps and GitLab.',
        'Planning and development of Python FastAPI backend architectures with React TypeScript frontend. Fully dockerised deployments of all system components. Implementation of RAG/GRAG pipelines for intelligent codebase analysis and integration of internal documents, manuals and training material.',
        'CI/CD automation through AI-controlled GitLab runners and executors, Taiga and Jira integration and Model Context Protocol (MCP) for multi-agent communication. Quality assurance with Explainable AI, Arize Phoenix, RAG evaluation, hallucination detection, monitoring and log analysis with R Shiny.'
      ]
    },
    {
      period: 'March 2025',
      title: 'Trainer, Association of Statutory Health Insurance Dentists Baden-Württemberg (KZV BW)',
      technologies: 'Python, PyTorch, CUDA, Docker, JupyterLab, Google Colab, Hugging Face',
      description: [
        'Theoretical and hands-on workshops on generative AI for project and team leads. Overview of the AI landscape, evaluation criteria, optimal use cases, classical ML, time-series analysis, signal processing and LLM-based solutions.',
        'Tailored edge-AI systems with RAG/GRAG architectures and LoRA/QLoRA fine-tuning, autonomous AI agents, chatbot solutions, hosting strategies, investment analysis, total cost of ownership, data anonymisation and EU AI Act compliance.'
      ]
    },
    {
      period: 'July 2024 - August 2024',
      title: 'Software Developer, IoT',
      technologies: 'Azure IoT, Digital Twins, Event Hubs, SQL database, Azure Functions, C# (.NET 8), REST APIs, gRPC, Docker',
      description: [
        'Development of a digital twin for water filters, a local Docker test environment and integration into Azure Cloud.'
      ]
    },
    {
      period: 'July 2024',
      title: 'Trainer, Publishing Sector',
      technologies: 'Python, PyTorch, CUDA, Docker, JupyterLab, Google Colab, Hugging Face',
      description: [
        'Theory and hands-on workshops on generative AI for project and team leads, focused on use-case selection, edge AI, RAG/GRAG, agents, chatbots, hosting strategies, data protection, EU AI Act and security concepts.'
      ]
    },
    {
      period: 'October 2023 - October 2025',
      title: 'Research and Development, AI and Robotics, Offenburg University',
      technologies: 'Python, PyTorch, LLMs, ChatGPT, RESTGPT, NanoGPT, Java, Android, REST API, Swagger, C#/C++, MS Azure, Azure DevOps Pipelines, HTML, CSS, JavaScript, Python Shiny',
      description: [
        'Development of AI methods for digital assistants and for detecting and classifying emotions from image and audio data. Dockerised prototypes with different GPT models, API implementations and training of local alternative LLMs based on open models with web and user-specific data. Monitoring with Shiny dashboards.',
        'Control of a telepresence robot system and linkage of the robot with server applications via REST APIs.'
      ],
      pageBreakBefore: true
    },
    {
      period: 'August 2022 - March 2024',
      title: 'Technical Lead, Scrum Master, International Banking Sector, T-Systems',
      technologies: 'FAME, Python, R, RMarkdown, Camunda, Docker, Kubernetes, GitLab, Confluence, Jira, R Shiny',
      description: [
        'Leadership of a team of software developers and data scientists in a large migration project in the international banking sector.',
        'Preparation and moderation of team meetings, retrospectives, Scrum of Scrums, release reviews and customer presentations. Coordination with requirements engineering, product owners and team leads.',
        'Development of an R library for standardised statistical reports by reimplementing existing FAME logic in R and Python. Camunda executors in Python, workflow configuration, GitLab pipelines and automated Confluence documentation.'
      ]
    },
    {
      period: 'May 2021 - July 2022',
      title: 'Software Developer, Software Architect, ERGO Group AG',
      technologies: 'C#, C++, Matlab',
      description: [
        'Reimplementation of Matlab software for valuation and management of financial products in C#. Market values and cashflows for bonds, mortgages, swaps, swaptions, callable bonds, floaters and steepeners. Architecture, Entity Framework, SQLite/SQL Server, CI/CD with Azure DevOps, Doxygen, C++/CLI, .NET 6, C++20 and unit/integration tests.'
      ]
    },
    {
      period: 'May 2019 - April 2021',
      title: 'Project Manager, Data Scientist, Software Developer, Printing Industry (B2B)',
      technologies: 'Xamarin Forms, Entity Framework Core, OpenCV, SkiaSharp, Pillow, Docker, Flask API, MS Azure DevOps',
      description: [
        'Lead role in developing an application for counterfeit detection and robust fingerprinting for patented security labels. Image processing with SIFT, SURF, ORB, Laplace and Fourier transforms, signal-to-noise analyses, CI deployment for Android and iOS, REST API integration, heartbeat service and response system.'
      ]
    },
    {
      period: 'February 2020 - June 2020',
      title: 'Data Scientist, Energy Technology (B2C)',
      technologies: 'Azure TSI, Azure Cosmos DB, Azure SQL Database, Azure Functions, Azure Key Vault, pyramid ARIMA, Scikit-Learn, TensorFlow-Keras, Confluence, Jira',
      description: [
        'Forecasting future energy consumption based on historical data and weather forecasts. Evaluation of ARIMA, Random Forest and LSTM with performance and quality metrics, Azure time-series processing, deployment in Azure Functions, daily reporting validation and Confluence documentation.'
      ]
    },
    {
      period: 'September 2019 - January 2020',
      title: 'Project Manager, Data Scientist, Software Developer, eCommerce (B2B)',
      technologies: 'Docker, MySQL, .NET Core, Swagger, R Shiny dashboard, ShinyProxy, Caddy',
      description: [
        'Concept and design of a security and authentication concept for an online-retail analytics platform. Implementation as Docker-based REST architecture.'
      ]
    },
    {
      period: 'May 2018 - June 2019',
      title: 'Data Scientist, Software Developer, Telecommunications (B2C)',
      technologies: 'KNIME, Impala Hadoop, Docker, R Shiny, ShinyProxy, Random Forests with Scikit-Learn, neural networks with TensorFlow',
      description: [
        'Process analysis with business units, raw-data discovery in Hadoop via Impala and KNIME, R logic for KPI aggregation, Shiny dashboards, NLP analysis of service calls, predictive models for field service and production ML implementation with CI and Docker.'
      ]
    },
    {
      period: 'April 2017 - May 2018',
      title: 'Software Developer, Industry and IoT (B2C)',
      technologies: 'Xamarin Forms, .NET, Bluetooth Low Energy',
      description: [
        'Development of a mobile app and PC software for programming and controlling time switches. Bluetooth stack, UDP discovery service, rollout tool and maintenance process with a three-person team.'
      ]
    },
    {
      period: '2015 - 2017',
      title: 'System Administrator, IT Group, Institute of Physics, Albert-Ludwigs-University Freiburg',
      description: [
        'Setup and maintenance of Windows and Linux images with ESXi and Xen. DHCP, CUPS, FOG server, LDAP authentication, KISS systems and UPS configurations.',
        'Automated penetration-test system for the institute network with NMAP script engine, Metasploit plugin based on CVE entries and IDS with Snort.'
      ]
    },
    {
      period: '2016',
      title: 'Research Associate, Institute of Physics, Albert-Ludwigs-University Freiburg',
      technologies: 'C++, CUDA, Python, R',
      description: [
        'Development and analysis of recognition rates of neural networks for pattern matching, including Boltzmann networks, LSTM networks, Hopfield and Jordan networks with different learning rules, activation functions and encodings, plus trained networks for baseline correction in NMS and ESR spectra.'
      ]
    },
    {
      period: '2012 - 2015',
      title: 'Research Associate, Institute of Physical Chemistry, Albert-Ludwigs-University Freiburg',
      technologies: 'MATLAB, TurboMol, ORCA, Gromacs MD',
      description: [
        'QM/MM simulations of ESR and NMR parameters for flavoproteins, CHARMM force fields, energy minimisation, lignin potential and coupling of thermal ensembles to the simulation box.'
      ]
    },
    {
      period: '2011 - 2012',
      title: 'Research Associate, Optics / Medical Technology and Pharmacy',
      technologies: 'Mathematica, Python, R, KNIME',
      description: [
        'Software for determining astigmatism and dioptre values of eye lenses with machine learning, including implementation of patent EP 2814916. Additional analysis of synergistic effects in cancer-drug combinations and implementation of the Chou Combination Index in R as a KNIME node.'
      ]
    },
    {
      period: '2010 - 2012',
      title: 'Financial Mathematics',
      technologies: 'C++, Python, R, Bash scripts',
      description: [
        'Programs for retrieving tick data for WKN codes and Bitcoin prices, swing-trading strategies for Bitcoin, DAX, Dow Jones and STOXX 50 and moving-average calculations.'
      ]
    }
  ]
};
